import { HEROES } from '@fukemon/shared';
import { Billboard, Float, Html, Sparkles } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import {
  Color,
  MathUtils,
  Mesh,
  MeshStandardMaterial,
  Vector3,
  type Group
} from 'three';
import { useGameStore } from '../state/gameStore';
import { ActiveCreature, Creature } from './Creature';

const cameraTarget = new Vector3();
const cameraPosition = new Vector3();

const ENEMY_SPAWNS: [number, number, number][] = [
  [-7, 0, -5],
  [7, 0, -5],
  [-8, 0, 2],
  [8, 0, 2],
  [0, 0, -6]
];

function BearBot() {
  return (
    <group>
      <mesh position={[0, 0.72, 0]} castShadow>
        <boxGeometry args={[0.85, 1.05, 0.58]} />
        <meshStandardMaterial color="#cf1748" roughness={0.5} metalness={0.65} />
      </mesh>
      <mesh position={[0, 1.48, 0]}>
        <boxGeometry args={[0.78, 0.55, 0.55]} />
        <meshStandardMaterial color="#231120" />
      </mesh>
      {[-0.25, 0.25].map((x) => (
        <mesh key={x} position={[x, 1.55, 0.3]}>
          <sphereGeometry args={[0.08, 8, 8]} />
          <meshBasicMaterial color="#ffea3d" />
        </mesh>
      ))}
      {[-0.27, 0.27].map((x) => (
        <mesh key={x} position={[x, 0.08, 0]}>
          <boxGeometry args={[0.25, 0.5, 0.34]} />
          <meshStandardMaterial color="#66142f" />
        </mesh>
      ))}
    </group>
  );
}

function PlayerCombat() {
  const player = useRef<Group>(null);
  const enemies = useRef<(Group | null)[]>([]);
  const slash = useRef<Group>(null);
  const pressed = useRef(new Set<string>());
  const lastAttack = useRef(0);
  const lastHit = useRef(0);
  const movement = useGameStore((state) => state.movement);
  const reduced = useGameStore((state) => state.reducedEffects);
  const { camera } = useThree();

  useEffect(() => {
    const down = (event: KeyboardEvent) => {
      pressed.current.add(event.code);
      if (event.code === 'Space' && !event.repeat) {
        event.preventDefault();
        useGameStore.getState().queueAttack();
      }
    };
    const up = (event: KeyboardEvent) => pressed.current.delete(event.code);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  useFrame(({ clock }, delta) => {
    if (!player.current) return;
    const x =
      (pressed.current.has('KeyD') || pressed.current.has('ArrowRight') ? 1 : 0) -
        (pressed.current.has('KeyA') || pressed.current.has('ArrowLeft') ? 1 : 0) +
      movement.x;
    const z =
      (pressed.current.has('KeyS') || pressed.current.has('ArrowDown') ? 1 : 0) -
        (pressed.current.has('KeyW') || pressed.current.has('ArrowUp') ? 1 : 0) +
      movement.z;

    const length = Math.hypot(x, z) || 1;
    const speed = 4.1 * delta;
    player.current.position.x = MathUtils.clamp(
      player.current.position.x + (x / length) * speed,
      -10,
      10
    );
    player.current.position.z = MathUtils.clamp(
      player.current.position.z + (z / length) * speed,
      -7,
      7
    );
    if (x || z) player.current.rotation.y = Math.atan2(x, z);

    const now = clock.elapsedTime;
    const queued = useGameStore.getState().combat.attackQueued;
    if (queued !== lastAttack.current) {
      lastAttack.current = queued;
      if (slash.current) {
        slash.current.visible = true;
        slash.current.scale.setScalar(0.35);
      }
      let closest: Group | null = null;
      let distance = 3.15;
      enemies.current.forEach((enemy) => {
        if (!enemy) return;
        const nextDistance = enemy.position.distanceTo(player.current!.position);
        if (nextDistance < distance) {
          closest = enemy;
          distance = nextDistance;
        }
      });
      if (closest) {
        const defeated = closest as Group;
        defeated.position.set(
          (Math.random() > 0.5 ? 1 : -1) * (7 + Math.random() * 2),
          0,
          -5 + Math.random() * 9
        );
        const combat = useGameStore.getState().combat;
        const kills = combat.kills + 1;
        useGameStore.getState().setCombat({ kills, wave: 1 + Math.floor(kills / 5) });
      }
    }
    if (slash.current?.visible) {
      slash.current.rotation.z += delta * 9;
      slash.current.scale.addScalar(delta * 3.5);
      if (slash.current.scale.x > 1.2) slash.current.visible = false;
    }

    enemies.current.forEach((enemy, index) => {
      if (!enemy) return;
      const speed = (0.75 + useGameStore.getState().combat.wave * 0.08) * delta;
      const direction = cameraTarget.copy(player.current!.position).sub(enemy.position);
      direction.y = 0;
      if (direction.lengthSq() > 0.01) {
        direction.normalize();
        enemy.position.addScaledVector(direction, speed);
        enemy.rotation.y = Math.atan2(direction.x, direction.z);
      }
      if (enemy.position.distanceTo(player.current!.position) < 1.05 && now - lastHit.current > 0.7) {
        lastHit.current = now;
        const combat = useGameStore.getState().combat;
        const hp = Math.max(0, combat.hp - 12);
        if (hp === 0) {
          useGameStore.getState().setCombat({ hp: 100, kills: 0, wave: 1 });
          player.current!.position.set(0, 0, 3.8);
          enemies.current.forEach((item, enemyIndex) => item?.position.set(...ENEMY_SPAWNS[enemyIndex]));
        } else {
          useGameStore.getState().setCombat({ hp });
          enemy.position.set(...ENEMY_SPAWNS[index]);
        }
      }
    });

    cameraTarget.set(player.current.position.x, 0.9, player.current.position.z);
    cameraPosition.set(
      player.current.position.x + 4.8,
      reduced ? 6.2 : 5.4,
      player.current.position.z + 7.2
    );
    camera.position.lerp(cameraPosition, 1 - Math.pow(0.002, delta));
    camera.lookAt(cameraTarget);
  });

  return (
    <>
      <group ref={player} position={[0, 0, 3.8]}>
        <ActiveCreature />
        <group ref={slash} position={[0, 1, 0]} visible={false} rotation={[Math.PI / 2, 0, 0]}>
          <mesh>
            <torusGeometry args={[1.25, 0.13, 8, 28, Math.PI * 1.45]} />
            <meshBasicMaterial color="#fff236" transparent opacity={0.9} />
          </mesh>
        </group>
        <pointLight position={[0, 1.3, 0]} color="#f6c945" intensity={2.5} distance={5} />
      </group>
      {ENEMY_SPAWNS.map((position, index) => (
        <group
          key={index}
          ref={(node) => {
            enemies.current[index] = node;
          }}
          position={position}
        >
          <BearBot />
        </group>
      ))}
    </>
  );
}

function NeonSign({
  label,
  position,
  color
}: {
  label: string;
  position: [number, number, number];
  color: string;
}) {
  return (
    <Billboard position={position}>
      <Html transform distanceFactor={8} center zIndexRange={[4, 0]}>
        <div className="world-sign" style={{ borderColor: color, color }}>
          {label}
        </div>
      </Html>
    </Billboard>
  );
}

function Terminal({
  position,
  symbol,
  color
}: {
  position: [number, number, number];
  symbol: string;
  color: string;
}) {
  return (
    <group position={position}>
      <mesh position={[0, 0.65, 0]} rotation={[-0.12, 0, 0]}>
        <boxGeometry args={[1.5, 1.25, 0.3]} />
        <meshStandardMaterial color="#171425" metalness={0.55} roughness={0.5} />
      </mesh>
      <mesh position={[0, 0.76, 0.18]} rotation={[-0.12, 0, 0]}>
        <planeGeometry args={[1.15, 0.75]} />
        <meshBasicMaterial color={color} />
      </mesh>
      <Html transform position={[0, 0.75, 0.2]} distanceFactor={7} center zIndexRange={[4, 0]}>
        <div className="terminal-label">{symbol}</div>
      </Html>
      <mesh position={[0, 0.12, 0]}>
        <boxGeometry args={[0.8, 0.35, 0.75]} />
        <meshStandardMaterial color="#28213c" />
      </mesh>
    </group>
  );
}

function Arena() {
  return (
    <group position={[6.6, 0, -2.6]}>
      <mesh receiveShadow>
        <cylinderGeometry args={[3, 3.35, 0.45, 16]} />
        <meshStandardMaterial color="#25172e" roughness={0.82} />
      </mesh>
      <mesh position={[0, 0.25, 0]}>
        <torusGeometry args={[2.55, 0.15, 8, 24]} />
        <meshBasicMaterial color="#ff3b66" />
      </mesh>
      <Float speed={1.6} floatIntensity={0.22} rotationIntensity={0.14}>
        <group position={[0, 0.46, 0]}>
          <Creature
            hero={{
              ...HEROES[4],
              name: 'MARGIN CALLOSSUS',
              color: '#7c3aed',
              accent: '#ff3b66'
            }}
            scale={1.75}
          />
        </group>
      </Float>
      <NeonSign label="RAID PIT" position={[0, 3.25, -1]} color="#ff3b66" />
    </group>
  );
}

function Building({
  position,
  scale,
  color
}: {
  position: [number, number, number];
  scale: [number, number, number];
  color: string;
}) {
  return (
    <mesh position={position} scale={scale} receiveShadow castShadow>
      <boxGeometry />
      <meshStandardMaterial color={color} roughness={0.86} metalness={0.1} />
    </mesh>
  );
}

export function ExchangeWorld() {
  const reduced = useGameStore((state) => state.reducedEffects);

  return (
    <>
      <color attach="background" args={['#05030a']} />
      <fog attach="fog" args={['#090512', 13, 30]} />
      <ambientLight intensity={0.8} color="#8b5cf6" />
      <directionalLight
        castShadow
        position={[4, 12, 8]}
        intensity={2.3}
        color="#f6c945"
        shadow-mapSize={[1024, 1024]}
      />
      <pointLight position={[-7, 4, -4]} color="#8b5cf6" intensity={25} distance={13} />
      <pointLight position={[8, 4, -2]} color="#ff3b66" intensity={22} distance={12} />

      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[28, 22, 28, 22]} />
        <meshStandardMaterial color="#11101a" roughness={0.92} />
      </mesh>
      <gridHelper args={[28, 28, new Color('#3e3452'), new Color('#1c1729')]} position={[0, 0.015, 0]} />

      <Building position={[0, 2.1, -7.8]} scale={[11, 2.2, 0.45]} color="#151020" />
      <Building position={[-10.8, 2.1, 0]} scale={[0.45, 2.2, 8]} color="#151020" />
      <Building position={[10.8, 2.1, 0]} scale={[0.45, 2.2, 8]} color="#151020" />
      <Building position={[-6.3, 0.55, -3.8]} scale={[3.2, 0.55, 1]} color="#251c32" />
      <Building position={[-6.4, 1.7, 5.2]} scale={[3.4, 1.7, 1.2]} color="#171425" />
      <Building position={[5.8, 1.9, 5.5]} scale={[3.3, 1.9, 1]} color="#191226" />

      <group position={[-5.9, 0, -2.9]}>
        <Terminal position={[-1.7, 0, 0]} symbol="SPY" color="#f6c945" />
        <Terminal position={[0, 0, 0]} symbol="NVDA" color="#b9ff45" />
        <Terminal position={[1.7, 0, 0]} symbol="GME" color="#ff3b66" />
        <NeonSign label="CURSED EXCHANGE" position={[0, 3.1, -0.6]} color="#f6c945" />
      </group>

      <group position={[-6.2, 0, 4.25]}>
        <mesh position={[0, 1.3, 0]}>
          <boxGeometry args={[5, 2.8, 0.5]} />
          <meshStandardMaterial color="#272039" metalness={0.5} />
        </mesh>
        <mesh position={[0, 1.3, 0.27]}>
          <circleGeometry args={[0.92, 16]} />
          <meshBasicMaterial color="#32d8d2" />
        </mesh>
        <NeonSign label="FUKÉVAULT" position={[0, 3.3, 0]} color="#32d8d2" />
      </group>

      <group position={[6.1, 0, 4.5]}>
        {[-1.3, 0, 1.3].map((x, index) => (
          <mesh key={x} position={[x, 0.75, 0]}>
            <boxGeometry args={[0.95, 1.5, 0.8]} />
            <meshStandardMaterial
              color={['#632e83', '#1d7388', '#81324f'][index]}
              emissive={['#321444', '#0d3640', '#451426'][index]}
              emissiveIntensity={0.7}
            />
          </mesh>
        ))}
        <NeonSign label="FLASH FOSSIL ARCADE" position={[0, 3.35, 0]} color="#8b5cf6" />
      </group>

      <Arena />
      {!reduced && <Sparkles count={38} scale={[22, 6, 16]} size={2} speed={0.25} color="#f6c945" />}
      <PlayerCombat />
    </>
  );
}
